// Nomenclatura do cliente: troca "produto/produtor/mesa" pelos termos configurados
// no branding do tenant (ex.: premiação de cases → "Case"/"Cooperativa"/"Sala").
// Mesmo formato do konq-backoffice (utils/nomenclatura.ts) — TenantBranding.NomenclaturaJson:
//   { produto, produtos, produtor, produtores, produtorFeminino, mesa, mesas, mesaMasculino }
// Função pura, usada pelo postProcessor do i18n e pelo nomenclatura.check.mjs.

const PADRAO = {
  produto: 'produto',
  produtos: 'produtos',
  produtor: 'produtor',
  produtores: 'produtores',
  mesa: 'mesa',
  mesas: 'mesas',
};

// Artigo/contração masculina → feminina (antes de produtor(es) com produtorFeminino).
const FEMININO = {
  o: 'a',
  os: 'as',
  do: 'da',
  dos: 'das',
  no: 'na',
  nos: 'nas',
  ao: 'à',
  aos: 'às',
  pelo: 'pela',
  pelos: 'pelas',
  um: 'uma',
  seu: 'sua',
  seus: 'suas',
  pro: 'pra',
  pros: 'pras',
};

// Artigo/contração/possessivo feminino → masculino (antes de mesa(s) com mesaMasculino).
const MASCULINO = {
  a: 'o',
  as: 'os',
  da: 'do',
  das: 'dos',
  na: 'no',
  nas: 'nos',
  à: 'ao',
  às: 'aos',
  pela: 'pelo',
  pelas: 'pelos',
  uma: 'um',
  sua: 'seu',
  suas: 'seus',
  pra: 'pro',
  pras: 'pros',
  minha: 'meu',
  minhas: 'meus',
  esta: 'este',
  estas: 'estes',
  essa: 'esse',
  essas: 'esses',
  nesta: 'neste',
  nestas: 'nestes',
  nessa: 'nesse',
  nessas: 'nesses',
  desta: 'deste',
  destas: 'destes',
  dessa: 'desse',
  dessas: 'desses',
  nenhuma: 'nenhum',
};

// Possessivos: o artigo antes deles também concorda ("às minhas mesas"). Fora isso, só o
// determinante colado ao termo muda — em "atribuído a uma mesa" o "a" é preposição.
const POSSESSIVOS = new Set(['meu', 'meus', 'minha', 'minhas', 'seu', 'seus', 'sua', 'suas']);

const DETERMINANTES = [...new Set([...Object.keys(FEMININO), ...Object.keys(MASCULINO)])].sort(
  (a, b) => b.length - a.length,
);

// Limite de palavra com lookaround Unicode (\b não entende acento).
// "produtores|produtor" antes de "produtos|produto" e o lookahead garantem que
// "produto" nunca casa dentro de "produtor". Até dois determinantes antes do termo
// ("às minhas mesas") entram no match pra concordar com o gênero.
const REGEX = new RegExp(
  `(?<![\\p{L}\\p{N}])((?:(?:${DETERMINANTES.join('|')})\\s+){0,2})(produtores|produtor|produtos|produto|mesas|mesa)(?![\\p{L}\\p{N}])`,
  'giu',
);

// Replica a capitalização do original: TUDO MAIÚSCULO, Inicial Maiúscula ou minúscula.
function comCaixa(original, termo) {
  if (original.length > 1 && original === original.toUpperCase()) return termo.toUpperCase();
  if (original[0] === original[0].toUpperCase()) return termo[0].toUpperCase() + termo.slice(1).toLowerCase();
  return termo.toLowerCase();
}

// JSON do branding (string ou objeto) → termos completos, ou null quando não há config.
export function parseNomenclatura(json) {
  if (!json) return null;
  try {
    const cfg = typeof json === 'string' ? JSON.parse(json) : json;
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return null;
    const termos = { produtorFeminino: !!cfg.produtorFeminino, mesaMasculino: !!cfg.mesaMasculino };
    for (const k of Object.keys(PADRAO)) termos[k] = (typeof cfg[k] === 'string' && cfg[k].trim()) || PADRAO[k];
    return termos;
  } catch {
    return null;
  }
}

// Determinante no gênero do termo configurado (ou o original, se o gênero não muda).
function concordar(artigo, chave, termos) {
  const minusculo = artigo.toLowerCase();
  if (chave.startsWith('produtor') && termos.produtorFeminino && FEMININO[minusculo]) {
    return comCaixa(artigo, FEMININO[minusculo]);
  }
  if (chave.startsWith('mesa') && termos.mesaMasculino && MASCULINO[minusculo]) {
    return comCaixa(artigo, MASCULINO[minusculo]);
  }
  return artigo;
}

export function aplicarNomenclatura(texto, termos) {
  if (!termos || typeof texto !== 'string') return texto;
  return texto.replace(REGEX, (_m, determinantes, palavra) => {
    const chave = palavra.toLowerCase();
    const partes = [...determinantes.matchAll(/(\S+)(\s+)/gu)];
    const prefixo = partes
      .map(([, det, espaco], i) => {
        const proximo = partes[i + 1]?.[1];
        const concorda = !proximo || POSSESSIVOS.has(proximo.toLowerCase());
        return (concorda ? concordar(det, chave, termos) : det) + espaco;
      })
      .join('');
    return prefixo + comCaixa(palavra, termos[chave]);
  });
}
